/**
 * useOsintRunEvents — live SSE subscription for an OSINT investigation's run.
 *
 * Mirrors the deep-research `useHypothesisRunEvents`: uses
 * `@microsoft/fetch-event-source` (not native EventSource) so the bearer
 * token is sent, closes on `run_finished` and unmount, and invalidates the
 * investigation detail query when the run finishes. The OSINT SSE endpoint is
 * investigation-scoped, so this keys off (caseId, osintId).
 */

import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { apiClient } from '@/services/api/client'
import { type OsintRunEvent, osintApi } from '@/services/api/osint'
import { osintKeys } from './use-osint'

interface OsintEventsState {
  events: OsintRunEvent[]
  status: string | null
  isConnected: boolean
}

export function useOsintRunEvents(caseId: string, osintId: string | undefined): OsintEventsState {
  const [state, setState] = useState<OsintEventsState>({
    events: [],
    status: null,
    isConnected: false,
  })
  const qc = useQueryClient()

  useEffect(() => {
    if (!caseId || !osintId) return

    const ctrl = new AbortController()
    const url = osintApi.eventsUrl(caseId, osintId)
    const token = apiClient.getToken()

    fetchEventSource(url, {
      signal: ctrl.signal,
      openWhenHidden: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      credentials: 'include',
      onopen: async response => {
        if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
          setState(s => ({ ...s, isConnected: true }))
          return
        }
        throw new Error(`SSE handshake failed: ${response.status}`)
      },
      onmessage: raw => {
        try {
          const evt = JSON.parse(raw.data) as OsintRunEvent
          setState(s => ({
            ...s,
            events: [...s.events, evt],
            status:
              evt.type === 'run_status'
                ? ((evt as { status?: string }).status ?? s.status)
                : s.status,
          }))
          if (evt.type === 'run_finished') {
            qc.invalidateQueries({ queryKey: osintKeys.detail(caseId, osintId) })
            ctrl.abort()
          }
          // The agent paused for user input — push a refetch so the detail page
          // swaps to the clarification card without waiting on a poll. The
          // questions themselves ride the investigation's GET payload.
          if (evt.type === 'clarification_requested') {
            qc.invalidateQueries({ queryKey: osintKeys.detail(caseId, osintId) })
          }
        } catch {
          // ignore parse errors
        }
      },
      onclose: () => setState(s => ({ ...s, isConnected: false })),
      onerror: err => {
        setState(s => ({ ...s, isConnected: false }))
        throw err // opt out of the library's infinite retry
      },
    }).catch(() => {
      // rejection already surfaced via onerror state
    })

    return () => ctrl.abort()
  }, [caseId, osintId, qc])

  return state
}
